import type { TestLibraryType, TestListParams } from '@/lib/types'
import { readIeltsModule, type IeltsModule } from '@/domain/exam-classification'

/**
 * The exam library's filter state is URL-driven (ticket #283): any surface can
 * link into a pre-filtered library (`/exams-library?type=reading`) and the
 * library issues the matching server-filtered request for that state.
 */

export interface LibraryFilterState {
  page: number
  search: string
  testType?: TestLibraryType
  year?: number
  month?: number
  availableOnly?: boolean
  hideCompleted?: boolean
  ieltsModule?: IeltsModule
}

export const LIBRARY_PAGE_SIZE = 12

const LIBRARY_TYPES: ReadonlyArray<TestLibraryType> = ['full', 'listening', 'reading', 'writing', 'speaking']

function positiveInt(value: string | null): number | undefined {
  if (!value) return undefined
  const parsed = Number.parseInt(value, 10)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined
}

function enabledFlag(value: string | null): true | undefined {
  return value === '1' || value?.toLowerCase() === 'true' ? true : undefined
}

/** Invalid values are dropped, mirroring the server's silent-ignore behavior. */
export function parseLibraryParams(params: URLSearchParams): LibraryFilterState {
  const type = params.get('type')
  const month = positiveInt(params.get('month'))
  return {
    page: positiveInt(params.get('page')) ?? 1,
    search: params.get('q')?.trim() ?? '',
    testType: LIBRARY_TYPES.includes(type as TestLibraryType) ? (type as TestLibraryType) : undefined,
    year: positiveInt(params.get('year')),
    month: month !== undefined && month <= 12 ? month : undefined,
    availableOnly: enabledFlag(params.get('available')),
    hideCompleted: enabledFlag(params.get('hide_completed')),
    ieltsModule: readIeltsModule(params.get('module')) ?? undefined,
  }
}

export function serializeLibraryParams(state: LibraryFilterState): URLSearchParams {
  const params = new URLSearchParams()
  if (state.search) params.set('q', state.search)
  if (state.testType) params.set('type', state.testType)
  if (state.year !== undefined) params.set('year', String(state.year))
  if (state.month !== undefined) params.set('month', String(state.month))
  if (state.availableOnly) params.set('available', '1')
  if (state.hideCompleted) params.set('hide_completed', '1')
  if (state.ieltsModule) params.set('module', state.ieltsModule)
  if (state.page > 1) params.set('page', String(state.page))
  return params
}

/** Path + query for linking into a pre-filtered library from other surfaces. */
export function libraryPath(state: Partial<LibraryFilterState>): string {
  const query = serializeLibraryParams({ page: 1, search: '', ...state }).toString()
  return query ? `/exams-library?${query}` : '/exams-library'
}

export function toRequestParams(state: LibraryFilterState): TestListParams {
  return {
    page: state.page,
    limit: LIBRARY_PAGE_SIZE,
    ...(state.search ? { search: state.search } : {}),
    ...(state.testType ? { test_type: state.testType } : {}),
    ...(state.year !== undefined ? { year: state.year } : {}),
    ...(state.month !== undefined ? { month: state.month } : {}),
    ...(state.availableOnly ? { available_only: true } : {}),
    ...(state.hideCompleted ? { hide_completed: true } : {}),
    ...(state.ieltsModule ? { ielts_module: state.ieltsModule } : {}),
  }
}
